import { computed } from 'vue';
import { useTourFiltersStore } from '@/entities/filter/store/tour';
import { useToursActivities } from '@/entities/tour-activities';
import { useComponentI18n } from '@/shared/composables/useComponentI18n.ts';
import type ru from '../i18n/ru.json';

type TSchema = typeof ru;

export function useTourActivitiesTitle(messages: TSchema) {
  const { t } = useComponentI18n({ ru: messages });
  const filtersStore = useTourFiltersStore();
  const { toursActivities } = useToursActivities();

  const slugToName = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (toursActivities.value ?? []).forEach((a) => {
      if (a.slug) {
        map[a.slug] = a.name ?? a.slug;
      }
    });

    return map;
  });

  const selectedAfterSearchSlugs = computed<string[]>(() => filtersStore.searchState.toursActivities ?? []);
  const selectedBeforeSearchSlugs = computed<string[]>(() => filtersStore.state.toursActivities ?? []);

  const selectedLabels = computed<string[]>(() => selectedAfterSearchSlugs.value.map((s) => slugToName.value[s] ?? s));

  const isActive = computed(() => selectedAfterSearchSlugs.value.length > 0);
  const isDisabledResetButton = computed(() => selectedBeforeSearchSlugs.value.length === 0);

  const defaultTitle = t('tour-activities.title');

  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const normalizedLabels = computed(() => {
    return selectedLabels.value.map((label, index) => (index === 0 ? capitalize(label) : label.toLowerCase()));
  });

  const title = computed(() => {
    if (!normalizedLabels.value.length) {
      return defaultTitle;
    }

    return normalizedLabels.value.join(', ');
  });

  const itemsCount = computed(() => selectedAfterSearchSlugs.value.length);

  return { title, itemsCount, isActive, isDisabledResetButton };
}
